import React from "react";
import type { WatermarkConfig } from "../../api";

type Props = {
  value: WatermarkConfig;
  onChange: (next: WatermarkConfig) => void;
};

/**
 * Logo / Watermark — globaler Master-Switch.
 *
 * Position, Größe, Opazität und Eck-Abstand werden inzwischen pro Szene
 * im Scene-Inspector („🎯 Logo in dieser Scene") konfiguriert. Dieses
 * Panel besitzt nur noch:
 *
 *  - den Aktiv-Toggle (gilt fürs ganze Reel),
 *  - die Default-Werte, die neue Szenen übernehmen, solange du nichts
 *    überschreibst (Position/Größe/Opazität/Eck-Abstand).
 *
 * Das war die Konvention, die der Composer bisher als „globales" Logo
 * benutzt hat — wir lassen die Felder hier stehen, damit man bequem
 * vorab einen sinnvollen Default einstellt; sobald eine Scene per
 * Inspector oder Player-Drag eigene Werte bekommt, hat sie Vorrang.
 */
export const WatermarkPanel: React.FC<Props> = ({ value, onChange }) => {
  const set = <K extends keyof WatermarkConfig>(key: K, v: WatermarkConfig[K]): void =>
    onChange({ ...value, [key]: v });

  const isFreePlaced = typeof value.xPct === "number" && typeof value.yPct === "number";

  return (
    <div className="side-panel watermark-panel">
      <div className="side-panel-head">
        <h4>🎯 Logo / Watermark</h4>
        <button
          type="button"
          className={`toggle ${value.enabled ? "on" : "off"}`}
          onClick={() => set("enabled", !value.enabled)}
          aria-pressed={value.enabled}
        >
          <span className="dot" />
          <span className="lbl">{value.enabled ? "An" : "Aus"}</span>
        </button>
      </div>

      <div className="side-panel-hint" style={{ padding: "8px 4px 12px", color: "#94a3b8", fontSize: 12, lineHeight: 1.45 }}>
        Hier setzt du nur den globalen <strong>Aktiv-Toggle</strong> sowie die
        <em> Default-Werte für neue Szenen</em>. Die eigentliche Position, Größe,
        Opazität und der Eck-Abstand des Logos sind <strong>pro Szene</strong>
        einstellbar — entweder direkt im Player (Logo anfassen + ziehen, während
        die Szene ausgewählt ist) oder im Scene-Inspector unter
        <em> „🎯 Logo in dieser Scene"</em>.
      </div>

      <div className={`wm-grid ${value.enabled ? "" : "disabled"}`}>
        <label className="form-row">
          <span>
            Default-Position{" "}
            {isFreePlaced ? (
              <small style={{ color: "#94a3b8" }}>· frei platziert ({Math.round(value.xPct ?? 0)}% / {Math.round(value.yPct ?? 0)}%)</small>
            ) : null}
          </span>
          <div className="wm-pos-grid">
            {(["tl", "tr", "bl", "br"] as const).map((p) => (
              <button
                key={p}
                type="button"
                className={`wm-pos ${!isFreePlaced && value.position === p ? "active" : ""}`}
                onClick={() => {
                  onChange({ ...value, position: p, xPct: undefined, yPct: undefined, offsetX: 0, offsetY: 0 });
                }}
                title={p.toUpperCase()}
                disabled={!value.enabled}
              >
                <span className={`wm-dot ${p}`} />
              </button>
            ))}
          </div>
        </label>

        {isFreePlaced ? (
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => onChange({ ...value, xPct: undefined, yPct: undefined })}
            disabled={!value.enabled}
            style={{ alignSelf: "flex-start" }}
          >
            ↺ Auf Ecke zurücksetzen
          </button>
        ) : null}

        <label className="form-row">
          <span>Default-Größe ({value.size}px)</span>
          <input
            type="range"
            min={24}
            max={400}
            value={value.size}
            disabled={!value.enabled}
            onChange={(e) => set("size", Number(e.target.value))}
          />
        </label>

        <label className="form-row">
          <span>Default-Opazität ({Math.round(value.opacity * 100)}%)</span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(value.opacity * 100)}
            disabled={!value.enabled}
            onChange={(e) => set("opacity", Number(e.target.value) / 100)}
          />
        </label>

        {!isFreePlaced ? (
          <label className="form-row">
            <span>Default-Eck-Abstand ({value.paddingPx}px)</span>
            <input
              type="range"
              min={0}
              max={200}
              value={value.paddingPx}
              disabled={!value.enabled}
              onChange={(e) => set("paddingPx", Number(e.target.value))}
            />
          </label>
        ) : null}
      </div>
    </div>
  );
};
